import { isSmallScreenAtom } from '@/atoms/screenSize'
import { SecondaryPageLink, useSecondaryPage } from '@/PageManager'
import { toNote } from '@/lib/link'
import { getSeenEventRelayUrls } from '@/services/client.service'
import { pool } from '@nostr/gadgets/global'
import * as kinds from '@nostr/tools/kinds'
import { Event } from '@nostr/tools/wasm'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'
import { FormattedTimestamp } from './FormattedTimestamp'
import NoteOptions from './NoteOptions'
import Username from './Username'
import { tagNameEquals } from '@/lib/tag'

/**
 * Lists kind-11 forum topics of a kind-39000 group, sourced directly and
 * exclusively from the same relay the group metadata event came from.
 */
export default function ForumTopicList({ event }: { event: Event }) {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)

  const groupId = useMemo(() => event.tags.find(([name]) => name === 'd')?.[1], [event])
  const relay = useMemo(() => getSeenEventRelayUrls(event.id, event)[0], [event])

  const { value: topics } = useAsync(async () => {
    if (!groupId || !relay) return []
    return pool
      .querySync([relay], {
        kinds: [kinds.ForumThread],
        '#h': [groupId]
      })
      .then((topics) =>
        topics.sort((a, b) => b.created_at - a.created_at || a.id.localeCompare(b.id))
      )
  }, [groupId, relay])

  if (!groupId || !relay) return null

  return (
    <div className="min-h-[80vh]">
      <div className="divide-y">
        {(topics ?? []).map((topic) => (
          <div key={topic.id} className="px-4 py-3">
            <div className="flex items-center justify-between gap-1 text-sm text-muted-foreground">
              <SecondaryPageLink
                to={toNote(topic)}
                className="font-semibold text-lg truncate hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {topic.tags.find(tagNameEquals('title'))?.[1] ?? '<unnamed>'}
              </SecondaryPageLink>
              <NoteOptions event={topic} className="py-1 shrink-0 [&_svg]:size-5" />
            </div>
            <div className="flex items-center justify-between gap-1 text-sm text-muted-foreground">
              <Username
                user={topic.pubkey}
                className="flex truncate text-sm"
                skeletonClassName="h-3"
              />
              <FormattedTimestamp
                timestamp={topic.created_at}
                className="shrink-0"
                short={isSmallScreen}
                href={toNote(topic)}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  push(toNote(topic))
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="text-sm mt-2 text-center text-muted-foreground">
        {(topics ?? []).length === 0 && t('No topics yet')}
      </div>
    </div>
  )
}
